import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  Alert,
  AlertTitle
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { colors, fonts, spacing } from "../constants";
import { getDefaultRedirectPath } from "../utils/redirectUtils";
import logoImage from "../assets/Airports_Authority_of_India_logo.svg.png";
import { authService } from "../services/authService";
import { WebConfig } from "../web-config";

const Login = () => {
  const [searchParams] = useSearchParams();
  const [ssoLoading, setSsoLoading] = useState(false);
  const [errorState, setErrorState] = useState(null);
  const { loginWithSSO } = useAuth();
  const navigate = useNavigate();
  const isProcessingRef = useRef(false);
  const processedCodeRef = useRef(null);

  const handleSSOLogin = useCallback(
    async (code) => {
      // Prevent multiple calls with the same code
      if (isProcessingRef.current || processedCodeRef.current === code) {
        return;
      }

      isProcessingRef.current = true;
      processedCodeRef.current = code;
      setSsoLoading(true);

      // Clear any existing session before processing new SSO code
      // This ensures a clean login if user had an old session
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("ssoToken");

      try {
        // console.log('Starting SSO token exchange with code:', code.substring(0, 10) + '...');

        // Check if authService.ssoTokenExchange exists
        if (typeof authService.ssoTokenExchange !== "function") {
          throw new Error(
            "SSO service is not properly initialized. Please clear your browser cache and try again."
          );
        }

        const response = await authService.ssoTokenExchange(code);
        // console.log('SSO token exchange response:', response);

        // Check for specific error codes (102, 103) - show error with redirect option
        if (
          response.Code === "102" ||
          response.Code === "103" ||
          response.code === "102" ||
          response.code === "103"
        ) {
          const errorDetails = {
            timestamp: new Date().toISOString(),
            location: "Login.handleSSOLogin - Error Codes 102/103",
            errorCode: response.Code || response.code,
            errorMessage: response.status || response.message || response.error,
            fullResponse: response
          };
          // console.error('[SSO Frontend] SSO error codes 102/103 detected - Full Details:', errorDetails);
          // console.error('[SSO Frontend] Error Summary:', {
          // code: response.Code || response.code,
          // status: response.status,
          // error: response.error,
          // message: response.message,
          // details: response.details
          // });

          setSsoLoading(false);
          isProcessingRef.current = false;
          sessionStorage.removeItem("sso_redirect_attempt");

          // Build detailed error message from backend response
          const errorTitle = response.status || "SSO Authentication Error";
          const errorDetailsText =
            response.error || response.message || "SSO authentication failed";

          setErrorState({
            type: "sso_error",
            code: response.Code || response.code,
            message: errorTitle,
            description: `${errorDetailsText}. Your SSO session may have expired or the authorization code is no longer valid. Please try logging in again through the SSO portal.`
          });
          return;
        }

        if (response.success) {
          // Login with SSO data (stores tokens and user)
          // console.log('SSO response.data structure:', {
          // hasUser: !!response.data?.user,
          // hasOurTokens: !!response.data?.ourTokens,
          // hasSsoToken: !!response.data?.ssoToken,
          // keys: Object.keys(response.data || {})
          // });

          // console.log('Calling loginWithSSO...');
          const result = await loginWithSSO(response.data);
          // console.log('loginWithSSO returned:', result);

          if (result.success) {
            // Get user from response to determine redirect path
            // console.log('Getting user from response.data...');
            const user = response.data.user;
            // console.log('User:', user);

            // Access pending restriction disabled — users allowed even if role not mapped.
            /*
          // Check if access is restricted (pending admin approval)
          if (response.data.accessRestricted) {
            // console.log('[SSO Frontend] User access restricted - pending admin approval');
            isProcessingRef.current = false;
            sessionStorage.removeItem('sso_redirect_attempt');
            // Route to contact admin page
            navigate('/contact-admin', {
              state: {
                reason: 'pending_approval',
                user: user
              }
            });
            return;
          }
          */

            // console.log('Calling getDefaultRedirectPath...');
            const redirectPath = getDefaultRedirectPath(user);
            // console.log('Redirect path:', redirectPath);

            isProcessingRef.current = false;
            // Clear redirect flag on successful login
            sessionStorage.removeItem("sso_redirect_attempt");
            // console.log('SSO login successful, navigating to:', redirectPath);
            navigate(redirectPath);
          } else {
            // SSO authentication failed, show error with redirect option
            const errorDetails = {
              timestamp: new Date().toISOString(),
              location: "Login.handleSSOLogin - loginWithSSO failed",
              error: result.error,
              result: result
            };
            // console.error('[SSO Frontend] SSO authentication failed - Full Details:', errorDetails);
            // console.error('[SSO Frontend] Error Summary:', { error: result.error, success: result.success });

            setSsoLoading(false);
            isProcessingRef.current = false;
            sessionStorage.removeItem("sso_redirect_attempt");
            setErrorState({
              type: "auth_failed",
              message: "Authentication Failed",
              description:
                result.error ||
                "Unable to authenticate with the provided credentials. Please try logging in again through the SSO portal."
            });
          }
        } else {
          // SSO token exchange failed, show error with redirect option
          const errorDetails = {
            timestamp: new Date().toISOString(),
            location: "Login.handleSSOLogin - Token exchange failed",
            response: response,
            success: response.success,
            code: response.Code || response.code,
            status: response.status,
            error: response.error,
            message: response.message
          };
          // console.error('[SSO Frontend] SSO token exchange failed - Full Details:', errorDetails);
          // console.error('[SSO Frontend] Error Summary:', {
          // success: response.success,
          // code: response.Code || response.code,
          // status: response.status,
          // error: response.error,
          // message: response.message,
          // details: response.details
          // });

          setSsoLoading(false);
          isProcessingRef.current = false;
          sessionStorage.removeItem("sso_redirect_attempt");

          const errorMessage = response.status || "SSO Token Exchange Failed";
          const errorDetailsText =
            response.error ||
            response.message ||
            "Unable to exchange SSO token";

          setErrorState({
            type: "token_exchange_failed",
            message: errorMessage,
            description: `${errorDetailsText}. Please try logging in again through the SSO portal.`,
            code: response.Code || response.code
          });
        }
      } catch (error) {
        // Enhanced error logging for browser console
        const errorDetails = {
          timestamp: new Date().toISOString(),
          location: "Login.handleSSOLogin - catch block",
          error: {
            message: error.message,
            name: error.name,
            code: error.code,
            stack: error.stack
          },
          response: {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            headers: error.response?.headers
          },
          request: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
            headers: error.config?.headers
          }
        };

        // console.error('[SSO Frontend] SSO error caught - Full Error Details:', errorDetails);

        // Log exactly what we received from backend
        // console.log('[SSO Frontend] Backend error response received in Login component:', {
        // status: error.response?.status,
        // statusText: error.response?.statusText,
        // data: error.response?.data,
        // dataStringified: error.response?.data ? JSON.stringify(error.response?.data, null, 2) : 'null',
        // hasCode: !!(error.response?.data?.code || error.response?.data?.Code),
        // hasStatus: !!error.response?.data?.status,
        // hasError: !!error.response?.data?.error,
        // hasMessage: !!error.response?.data?.message,
        // hasDetails: !!error.response?.data?.details
        // });

        // console.error('[SSO Frontend] Error Summary:', {
        // message: error.message,
        // status: error.response?.status,
        // statusText: error.response?.statusText,
        // errorCode: error.response?.data?.code || error.response?.data?.Code,
        // errorMessage: error.response?.data?.error || error.response?.data?.status || error.response?.data?.message,
        // fullResponse: error.response?.data
        // });

        // Clear redirect flag before redirecting
        sessionStorage.removeItem("sso_redirect_attempt");

        // Check if error response has error codes
        if (
          error.response?.data?.Code === "102" ||
          error.response?.data?.Code === "103" ||
          error.response?.data?.code === "102" ||
          error.response?.data?.code === "103"
        ) {
          const ssoErrorDetails = {
            timestamp: new Date().toISOString(),
            location: "Login.handleSSOLogin - Error Codes 102/103 in catch",
            errorCode: error.response.data.Code || error.response.data.code,
            errorData: error.response.data
          };
          // console.error('[SSO Frontend] SSO error codes 102/103 detected in error response - Full Details:', ssoErrorDetails);
          // console.error('[SSO Frontend] Error Summary:', {
          // code: error.response.data.Code || error.response.data.code,
          // status: error.response.data.status,
          // error: error.response.data.error,
          // message: error.response.data.message,
          // details: error.response.data.details
          // });

          setSsoLoading(false);
          isProcessingRef.current = false;

          // Extract backend error details
          const errorData = error.response.data;
          const errorTitle = errorData.status || "SSO Authentication Error";
          const errorDetailsText =
            errorData.error || errorData.message || "SSO authentication failed";

          setErrorState({
            type: "sso_error",
            code: errorData.Code || errorData.code,
            message: errorTitle,
            description: `${errorDetailsText}. Your SSO session may have expired or the authorization code is no longer valid. Please try logging in again through the SSO portal.`
          });
        } else {
          // For network errors or other API errors, show error with option to try manual login
          const networkErrorDetails = {
            timestamp: new Date().toISOString(),
            location: "Login.handleSSOLogin - Network/Other Error",
            errorType:
              error.code === "ERR_NETWORK" ? "Network Error" : "Other Error",
            error: error
          };
          // console.error('[SSO Frontend] SSO failed with non-102/103 error - Full Details:', networkErrorDetails);
          // console.error('[SSO Frontend] Error Summary:', {
          // errorType: error.code === 'ERR_NETWORK' ? 'Network Error' : 'Other Error',
          // message: error.message,
          // code: error.code,
          // status: error.response?.status
          // });

          setSsoLoading(false);
          isProcessingRef.current = false;

          // Check if this is a function/initialization error
          const isFunctionError =
            error.message &&
            (error.message.includes("is not a function") ||
              error.message.includes("not properly initialized") ||
              error.message.includes("undefined"));

          setErrorState({
            type: "network_error",
            message: isFunctionError ? "Application Error" : "Connection Error",
            description: isFunctionError
              ? "The application needs to be refreshed. Please clear your browser cache (Ctrl+Shift+R or Cmd+Shift+R) and try again. If the problem persists, the application may need to be redeployed."
              : error.message ||
                "Unable to connect to the authentication server. This could be due to network issues or server unavailability.",
            showManualLogin: true
          });
        }
      }
    },
    [loginWithSSO, navigate]
  );

  // Handle SSO code from query parameter
  useEffect(() => {
    const code = searchParams.get("code");

    if (code) {
      // SSO code present, process SSO login (stays on /login route)
      // Only process if not already processing and code hasn't been processed
      if (!isProcessingRef.current && processedCodeRef.current !== code) {
        // console.log('SSO code detected, starting SSO login process');
        handleSSOLogin(code);
      } else {
        // console.log('SSO code already being processed or already processed');
      }
    } else {
      // No SSO code - only redirect if we're not currently processing SSO
      // This prevents redirecting while SSO is being processed
      if (isProcessingRef.current) {
        // console.log('No code but SSO is processing, waiting...');
        return;
      }
      // No SSO code, check if we should redirect to external SSO portal
      // Only redirect if:
      // 1. Not currently processing SSO
      // 2. We're on the QMS domain (not already on SSO portal)
      // 3. We haven't just redirected (prevent infinite loop)
      // 4. We're NOT in development mode (localhost or local IP)
      if (!isProcessingRef.current) {
        const currentHost = window.location.hostname;
        const ssoHost = WebConfig.REACT_APP_AAI_SSO_BASE_URL.replace(
          /^https?:\/\//,
          ""
        );
        const referrer = document.referrer;
        const hasRedirected = sessionStorage.getItem("sso_redirect_attempt");

        // Check if we're in development mode (localhost or local IP)
        const isDevelopment =
          currentHost === "localhost" ||
          currentHost === "127.0.0.1" ||
          currentHost.startsWith("192.168.") ||
          currentHost.startsWith("10.") ||
          currentHost.startsWith("172.") ||
          process.env.NODE_ENV === "development";

        // Manual login disabled
        // In development, always go to manual login
        if (isDevelopment) {
          navigate("/login/manual", { replace: true });
          return;
        }

        // Only redirect if:
        // - We're on QMS domain (not SSO portal)
        // - We haven't just redirected in this session
        // - Referrer is not the SSO portal (to prevent loop)
        if (
          currentHost !== ssoHost &&
          !hasRedirected &&
          (!referrer || !referrer.includes(ssoHost))
        ) {
          // Mark that we're redirecting to prevent loop
          sessionStorage.setItem("sso_redirect_attempt", "true");
          // Clear the flag after a delay to allow normal redirects later
          const timeoutId = setTimeout(() => {
            sessionStorage.removeItem("sso_redirect_attempt");
          }, 2000);

          // Try to redirect to SSO portal
          // Manual login disabled
          // If SSO portal has errors, user can still use manual login
          try {
            window.location.href = `${WebConfig.REACT_APP_AAI_SSO_BASE_URL}`;
          } catch (error) {
            // console.error('Failed to redirect to SSO portal:', error);
            // Manual login disabled
            // Fallback to manual login if redirect fails
            // clearTimeout(timeoutId);
            // sessionStorage.removeItem('sso_redirect_attempt');
            // navigate('/login/manual', { replace: true });
          }

          // Cleanup timeout on unmount
          return () => {
            clearTimeout(timeoutId);
          };
        } else {
          // Manual login disabled
          // If we're already on SSO portal or just came from it, redirect to manual login
          // This prevents redirect loops and provides fallback if SSO portal has errors
          // sessionStorage.removeItem('sso_redirect_attempt');
          // navigate('/login/manual', { replace: true });
        }
      }
    }
  }, [searchParams, handleSSOLogin, navigate]);

  // Handle SSO redirect to external portal
  const handleSSORedirect = () => {
    try {
      window.location.href = `${WebConfig.REACT_APP_AAI_SSO_BASE_URL}`;
    } catch (error) {
      // console.error('Failed to redirect to SSO portal:', error);
      setErrorState({
        type: "redirect_failed",
        message: "Redirect Failed",
        description:
          "Unable to redirect to SSO portal. Please try opening the link manually or contact support.",
        showManualLogin: true
      });
    }
  };

  // Manual login disabled
  const handleManualLogin = () => {
    navigate("/login/manual", { replace: true });
  };

  // Show error state with options
  if (errorState) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          overflow: "hidden",
          backgroundColor: colors.background
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: { xs: spacing.lg, md: spacing.xl },
            backgroundColor: colors.backgroundWhite,
            height: "100vh"
          }}
        >
          <Container
            maxWidth="sm"
            sx={{
              width: "100%",
              py: 0,
              px: { xs: spacing.md, md: spacing.lg }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: spacing.xl
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  mb: spacing.xl,
                  width: "100%"
                }}
              >
                <Box
                  component="img"
                  src={logoImage}
                  alt="Airports Authority of India logo"
                  sx={{
                    height: { xs: 44, md: 56 },
                    width: "auto"
                  }}
                />
              </Box>

              {/* Error Alert */}
              <Alert
                severity="error"
                sx={{
                  width: "100%",
                  mb: spacing.lg,
                  "& .MuiAlert-message": {
                    width: "100%"
                  }
                }}
              >
                <AlertTitle sx={{ fontWeight: fonts.weightSemiBold }}>
                  {errorState.message}
                </AlertTitle>
                <Typography variant="body2" sx={{ mb: spacing.sm }}>
                  {errorState.description}
                </Typography>
                {errorState.code && (
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textSecondary }}
                  >
                    Error Code: {errorState.code}
                  </Typography>
                )}
              </Alert>

              {/* Action Buttons */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: spacing.md
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSSORedirect}
                  sx={{
                    py: spacing.md,
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: fonts.weightMedium,
                    backgroundColor: colors.primary,
                    "&:hover": {
                      backgroundColor: colors.primaryDark
                    }
                  }}
                >
                  Try SSO Login Again
                </Button>

                {/* Manual login disabled */}

                {errorState.showManualLogin && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleManualLogin}
                    sx={{
                      py: spacing.md,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: fonts.weightMedium,
                      borderColor: colors.primary,
                      color: colors.primary,
                      "&:hover": {
                        borderColor: colors.primaryDark,
                        backgroundColor: "rgba(0, 82, 204, 0.04)"
                      }
                    }}
                  >
                    Use Manual Login Instead
                  </Button>
                )}
              </Box>

              {/* Help Text */}
              <Typography
                variant="body2"
                sx={{
                  color: colors.textSecondary,
                  mt: spacing.lg,
                  textAlign: "center"
                }}
              >
                If the problem persists, please contact your system
                administrator
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  // Show loader if SSO is processing (route stays as /login)
  if (ssoLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          overflow: "hidden",
          backgroundColor: colors.background
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: { xs: spacing.lg, md: spacing.xl },
            backgroundColor: colors.backgroundWhite,
            height: "100vh"
          }}
        >
          <Container
            maxWidth="sm"
            sx={{
              width: "100%",
              py: 0,
              px: { xs: spacing.md, md: spacing.lg }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: spacing.xl
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  mb: spacing.lg,
                  width: "100%"
                }}
              >
                <Box
                  component="img"
                  src={logoImage}
                  alt="Airports Authority of India logo"
                  sx={{
                    height: { xs: 44, md: 56 },
                    width: "auto"
                  }}
                />
              </Box>
              <CircularProgress size={48} sx={{ mb: spacing.md }} />
              <Typography
                variant="h6"
                sx={{
                  color: colors.textPrimary,
                  fontWeight: fonts.weightMedium
                }}
              >
                Authenticating...
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.textSecondary,
                  mt: spacing.sm
                }}
              >
                Please wait while we verify your credentials
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  // If no SSO code, redirect to manual login (handled in useEffect)
  return null;
};

export default Login;
